import { useCallback, useEffect, useMemo, useState } from 'react';
import { computeTotals, DEFAULT_VAT_RATE, nextQuoteNumber } from '../utils/quoteMath';

const STORAGE_KEY = 'elnaf_b2b_quote_draft';

const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (n) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);

const emptyCustomer = () => ({
  company: '', contact: '', email: '', phone: '',
  vatId: '', address: '', postcode: '', city: '', country: '',
});

const emptyQuote = () => ({
  quoteNo: '',            // assigned lazily on first line / on export
  date: today(),
  validUntil: plusDays(30),
  subject: '',            // headline printed above the items table
  lines: [],
  shipping: { carrier: '', cost: 0, country: '', currency: 'EUR' },
  vatRate: DEFAULT_VAT_RATE,
  customer: emptyCustomer(),
  notes: '',
});

function loadDraft() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyQuote();
    const parsed = JSON.parse(raw);
    // Shallow-merge so a schema addition never breaks an old draft
    return {
      ...emptyQuote(),
      ...parsed,
      shipping: { ...emptyQuote().shipping, ...(parsed.shipping || {}) },
      customer: { ...emptyCustomer(), ...(parsed.customer || {}) },
      lines: Array.isArray(parsed.lines) ? parsed.lines : [],
    };
  } catch {
    return emptyQuote();
  }
}

/**
 * Quote draft state for the B2B builder. Survives page refresh so a
 * half-built quote is never lost mid-call with a customer.
 */
export function useQuote() {
  const [quote, setQuote] = useState(loadDraft);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(quote)); } catch { /* quota / private mode */ }
  }, [quote]);

  /** Assign a quote number on first use so numbers aren't burned on empty drafts. */
  const ensureQuoteNo = useCallback(() => {
    setQuote((q) => (q.quoteNo ? q : { ...q, quoteNo: nextQuoteNumber() }));
  }, []);

  const addLine = useCallback((product, qty = 1) => {
    setQuote((q) => {
      const quoteNo = q.quoteNo || nextQuoteNumber();
      const idx = q.lines.findIndex((l) => l.sku === product.SKU);
      if (idx >= 0) {
        const lines = [...q.lines];
        lines[idx] = { ...lines[idx], qty: (Number(lines[idx].qty) || 0) + qty };
        return { ...q, quoteNo, lines };
      }
      return {
        ...q,
        quoteNo,
        lines: [
          ...q.lines,
          {
            sku: product.SKU,
            name: product.name,
            description: product.description || product.name,
            brand: product.brand,
            category: product.category || '',
            ean: product.ean || '',
            price: Number(product.price) || 0,
            listPrice: Number(product.price) || 0, // keep the catalog price for reference
            qty: Math.max(1, qty),
            discountPct: 0,
            weight: Number(product.weight) || 0,
            stock: Number(product.stock) || 0,
          },
        ],
      };
    });
  }, []);

  const updateLine = useCallback((sku, patch) => {
    setQuote((q) => ({
      ...q,
      lines: q.lines.map((l) => (l.sku === sku ? { ...l, ...patch } : l)),
    }));
  }, []);

  const removeLine = useCallback((sku) => {
    setQuote((q) => ({ ...q, lines: q.lines.filter((l) => l.sku !== sku) }));
  }, []);

  const setQty = useCallback((sku, qty) => {
    const n = Math.max(1, Number(qty) || 1);
    setQuote((q) => ({ ...q, lines: q.lines.map((l) => (l.sku === sku ? { ...l, qty: n } : l)) }));
  }, []);

  const setField = useCallback((field, value) => {
    setQuote((q) => ({ ...q, [field]: value }));
  }, []);

  const setCustomerField = useCallback((field, value) => {
    setQuote((q) => ({ ...q, customer: { ...q.customer, [field]: value } }));
  }, []);

  const setShipping = useCallback((patch) => {
    setQuote((q) => ({ ...q, shipping: { ...q.shipping, ...patch } }));
  }, []);

  const clearLines = useCallback(() => {
    setQuote((q) => ({ ...q, lines: [] }));
  }, []);

  const resetQuote = useCallback(() => {
    setQuote(emptyQuote());
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  }, []);

  const totals = useMemo(
    () => computeTotals(quote.lines, quote.shipping?.cost, quote.vatRate),
    [quote.lines, quote.shipping, quote.vatRate]
  );

  const skuQty = useMemo(() => {
    const m = {};
    quote.lines.forEach((l) => { m[l.sku] = l.qty; });
    return m;
  }, [quote.lines]);

  return {
    quote, totals, skuQty,
    addLine, updateLine, removeLine, setQty,
    setField, setCustomerField, setShipping,
    clearLines, resetQuote, ensureQuoteNo,
  };
}

export default useQuote;
